import { Button, Stack, Typography, useTheme } from "@mui/material";
import { Check, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { ObjPathProxy } from "ts-object-path";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import type {
	MyPlannResourceDictionary,
	PortfolioAddSuccessTranslations,
} from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannTopBarRedesign } from "web_myplann/Shared/Components/Redesign/MyPlannTopBarRedesign";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";

type Props = {
	namespace: ObjPathProxy<
		MyPlannResourceDictionary,
		PortfolioAddSuccessTranslations
	>;
};

export const PortfolioAddSuccessPage = ({ namespace }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const handleAddAnother = () => {
		navigate(Routing.Portfolio.PortfolioAddItem.path(), { replace: true });
	};

	const handleBackToPortfolio = () => {
		navigate(Routing.Portfolio.PortfolioList.path(), { replace: true });
	};

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.grey[50]}
			topBarBgColor={theme.palette.background.paper}
			topBar={<MyPlannTopBarRedesign title={t(namespace.successTitle)} />}
		>
			<Stack px={1.6} pt={4} pb={3.2} gap={3.2} alignItems="stretch">
				<Stack alignItems="center" textAlign="center" gap={1.6}>
					<Stack
						sx={{
							width: 64,
							height: 64,
							borderRadius: "50%",
							bgcolor: theme.palette.green.light,
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Check size={32} color={theme.palette.green.main} strokeWidth={2} />
					</Stack>
					<Stack gap={0.8} alignItems="center">
						<Typography variant="h2">{t(namespace.successHeadline)}</Typography>
						<Typography variant="body2">
							{t(namespace.successDescription)}
						</Typography>
						<Typography variant="hint">{t(namespace.successHint)}</Typography>
					</Stack>
				</Stack>

				<Stack gap={1.2}>
					<Button
						variant="primary"
						type="button"
						onClick={handleAddAnother}
						startIcon={<Plus size={20} />}
					>
						{t(namespace.successAddAnother)}
					</Button>
					<Button
						variant="secondary"
						type="button"
						onClick={handleBackToPortfolio}
					>
						{t(namespace.successBackToPortfolio)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannLayout>
	);
};
